import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { createAdminTicketLink, getOrder, sendPaymentLink, sendSponsorInvoice, markOrderPaid, refundOrder, syncMollie, syncMollieInvoice, refulfillOrder, listEmailSends, listInbound, updateSponsorInvoiceDetails } from "@/lib/api";
import { fmtEuro, fmtDateTime, fmtRelative, statusLabel, flowLabel, emailStatusLabel, emailStatusClass } from "@/lib/formatters";

function invoiceStatusLabel(status: string | null | undefined): string {
  const map: Record<string, string> = {
    draft: "Concept",
    issued: "Verzonden",
    open: "Open",
    overdue: "Te laat",
    paid: "Betaald",
    canceled: "Geannuleerd",
    cancelled: "Geannuleerd",
  };
  return status ? map[status] ?? status : "Nog niet aangemaakt";
}

interface SponsorInvoiceForm {
  company: string;
  kvk_number: string;
  vat_number: string;
  street_and_number: string;
  postal_code: string;
  city: string;
  country: string;
}

function readAddress(addr: unknown, keys: string[]): string {
  if (!addr || typeof addr !== "object") return "";
  const record = addr as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function sponsorInvoiceFormFromOrder(order: any): SponsorInvoiceForm {
  return {
    company: order?.buyer_company ?? "",
    kvk_number: order?.kvk_number ?? "",
    vat_number: order?.vat_number ?? "",
    street_and_number: readAddress(order?.billing_address, ["street_and_number", "streetAndNumber", "street", "address"]),
    postal_code: readAddress(order?.billing_address, ["postal_code", "postalCode", "postcode", "zip"]),
    city: readAddress(order?.billing_address, ["city", "plaats"]),
    country: readAddress(order?.billing_address, ["country", "countryCode", "country_code"]) || "NL",
  };
}

function hasCompleteSponsorInvoiceDetails(form: SponsorInvoiceForm): boolean {
  return !!form.company.trim() &&
    (!!form.kvk_number.trim() || !!form.vat_number.trim()) &&
    !!form.street_and_number.trim() &&
    !!form.postal_code.trim() &&
    !!form.city.trim();
}

function supportsBusinessInvoice(flow: string): boolean {
  return flow === "sponsor" || flow === "ambassador";
}

export function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [toast, setToast] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [payLinkMessage, setPayLinkMessage] = useState("");
  const [ticketLinkBusy, setTicketLinkBusy] = useState<string | null>(null);
  const [invoiceForm, setInvoiceForm] = useState<SponsorInvoiceForm>({
    company: "",
    kvk_number: "",
    vat_number: "",
    street_and_number: "",
    postal_code: "",
    city: "",
    country: "NL",
  });

  const { data: order, isLoading, error } = useQuery({
    queryKey: ["order", id],
    queryFn: () => getOrder(id!),
    enabled: !!id,
  });

  const { data: orderEmails } = useQuery({
    queryKey: ["order-emails", id],
    queryFn: () => listEmailSends({ orderId: id!, limit: 50 }),
    enabled: !!id,
  });

  const { data: orderInbound } = useQuery({
    queryKey: ["order-inbound", id],
    queryFn: async () => {
      const all = await listInbound({ limit: 100 });
      return all.filter((m) => m.matched_order_id === id);
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (order && supportsBusinessInvoice(order.flow)) setInvoiceForm(sponsorInvoiceFormFromOrder(order));
  }, [order]);

  const payLinkMut = useMutation({
    mutationFn: () => sendPaymentLink(id!, payLinkMessage),
    onSuccess: () => {
      setToast({ kind: "success", text: "Betaallink verstuurd." });
      setPayLinkMessage("");
    },
    onError: (e: Error) => setToast({ kind: "error", text: e.message }),
  });

  const markPaidMut = useMutation({
    mutationFn: () => markOrderPaid(id!),
    onSuccess: (r) => {
      const msg = r.already_paid
        ? "Order was al betaald."
        : `Order gemarkeerd als ${r.status}. ${r.tickets_created} ticket(s) gegenereerd.`;
      setToast({ kind: "success", text: msg });
      qc.invalidateQueries({ queryKey: ["order", id] });
    },
    onError: (e: Error) => setToast({ kind: "error", text: e.message }),
  });

  const refundMut = useMutation({
    mutationFn: (args: { amountCents?: number; reason?: string }) =>
      refundOrder(id!, args.amountCents, args.reason),
    onSuccess: (r) => {
      setToast({
        kind: "success",
        text: r.full_refund
          ? `✓ Volledig refund (${(r.amount_cents / 100).toFixed(2)}). Tickets gevoid.`
          : `✓ Partial refund van €${(r.amount_cents / 100).toFixed(2).replace(".", ",")} verwerkt.`,
      });
      qc.invalidateQueries({ queryKey: ["order", id] });
    },
    onError: (e: Error) => setToast({ kind: "error", text: e.message }),
  });

  const syncMut = useMutation({
    mutationFn: () => syncMollie(id!),
    onSuccess: (r) => {
      const map: Record<string, string> = {
        paid_and_fulfilled: "Order is nu betaald + tickets gegenereerd.",
        already_paid: "Mollie zegt betaald, maar order is al verwerkt.",
        cancelled: "Mollie zegt gecanceld → order op cancelled gezet.",
        noop: `Mollie status: ${r.mollie_status} — geen actie nodig.`,
      };
      setToast({ kind: "success", text: map[r.action] ?? `Status: ${r.status} (${r.mollie_status})` });
      qc.invalidateQueries({ queryKey: ["order", id] });
    },
    onError: (e: Error) => setToast({ kind: "error", text: e.message }),
  });

  const syncInvoiceMut = useMutation({
    mutationFn: () => syncMollieInvoice(id!),
    onSuccess: (r) => {
      const map: Record<string, string> = {
        invoice_paid_and_fulfilled: "Mollie factuur is betaald; tickets zijn gegenereerd.",
        already_paid: "Factuur/order was al als betaald verwerkt.",
        invoice_cancelled: "Mollie factuur is geannuleerd; order is geannuleerd.",
        already_cancelled: "Order was al geannuleerd.",
        invoice_synced: `Mollie factuurstatus: ${invoiceStatusLabel(r.mollie_invoice_status)}.`,
        noop: `Mollie factuurstatus: ${invoiceStatusLabel(r.mollie_invoice_status)}.`,
      };
      setToast({ kind: "success", text: map[r.action] ?? `Factuurstatus: ${invoiceStatusLabel(r.mollie_invoice_status)}` });
      qc.invalidateQueries({ queryKey: ["order", id] });
    },
    onError: (e: Error) => setToast({ kind: "error", text: e.message }),
  });

  const refulfillMut = useMutation({
    mutationFn: () => refulfillOrder(id!),
    onSuccess: (r) => {
      const ambMsg = r.ambassador_linked ? " + ambassadeur-record gelinkt" : "";
      setToast({ kind: "success", text: `${r.tickets_created} ticket(s) gegenereerd${ambMsg}.` });
      qc.invalidateQueries({ queryKey: ["order", id] });
    },
    onError: (e: Error) => setToast({ kind: "error", text: e.message }),
  });

  const invoiceMut = useMutation({
    mutationFn: () => sendSponsorInvoice(id!),
    onSuccess: (r) => {
      const invoiceText = r.invoice_no ? ` (${r.invoice_no})` : "";
      const isDraft = r.mollie_sales_invoice_status === "draft";
      const text = isDraft
        ? `Mollie conceptfactuur aangemaakt${invoiceText}. Testmode mailt alleen naar geverifieerde Mollie-organisatiegebruikers.`
        : `Mollie factuur verstuurd${invoiceText}.`;
      setToast({ kind: "success", text });
      qc.invalidateQueries({ queryKey: ["order", id] });
    },
    onError: (e: Error) => setToast({ kind: "error", text: e.message }),
  });

  const invoiceDetailsMut = useMutation({
    mutationFn: () => updateSponsorInvoiceDetails(id!, {
      company: invoiceForm.company,
      kvk_number: invoiceForm.kvk_number,
      vat_number: invoiceForm.vat_number,
      billing_address: {
        street_and_number: invoiceForm.street_and_number,
        postal_code: invoiceForm.postal_code,
        city: invoiceForm.city,
        country: invoiceForm.country,
      },
    }),
    onSuccess: () => {
      setToast({ kind: "success", text: "Factuurgegevens opgeslagen." });
      qc.invalidateQueries({ queryKey: ["order", id] });
    },
    onError: (e: Error) => setToast({ kind: "error", text: e.message }),
  });

  const saveAndInvoiceMut = useMutation({
    mutationFn: async () => {
      await updateSponsorInvoiceDetails(id!, {
        company: invoiceForm.company,
        kvk_number: invoiceForm.kvk_number,
        vat_number: invoiceForm.vat_number,
        billing_address: {
          street_and_number: invoiceForm.street_and_number,
          postal_code: invoiceForm.postal_code,
          city: invoiceForm.city,
          country: invoiceForm.country,
        },
      });
      return await sendSponsorInvoice(id!);
    },
    onSuccess: (r) => {
      const invoiceText = r.invoice_no ? ` (${r.invoice_no})` : "";
      setToast({ kind: "success", text: `Factuurgegevens opgeslagen + Mollie factuur verstuurd${invoiceText}.` });
      qc.invalidateQueries({ queryKey: ["order", id] });
    },
    onError: (e: Error) => setToast({ kind: "error", text: e.message }),
  });

  async function openTicketAccess(target: { orderId?: string; ticketId?: string }, mode: "page" | "pdf") {
    const idForBusy = target.ticketId ?? target.orderId ?? "order";
    const busyKey = `${mode}:${idForBusy}`;
    setTicketLinkBusy(busyKey);
    try {
      const link = await createAdminTicketLink(target);
      window.open(mode === "page" ? link.ticket_url : link.pdf_url, "_blank", "noopener");
    } catch (err) {
      setToast({ kind: "error", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setTicketLinkBusy(null);
    }
  }

  if (isLoading) return <div className="empty"><span className="spinner" /> Laden…</div>;
  if (error || !order) return <div className="empty">Bestelling niet gevonden.</div>;

  const canEditSponsorInvoice = supportsBusinessInvoice(order.flow) && !["paid", "invoice_paid", "refunded", "cancelled"].includes(order.status);
  const sponsorInvoiceReady = hasCompleteSponsorInvoiceDetails(invoiceForm);
  const canCreateOrIssueInvoice = supportsBusinessInvoice(order.flow) &&
    (order.status === "draft" || order.status === "pending") &&
    (!order.mollie_sales_invoice_id || order.mollie_sales_invoice_status === "draft");

  return (
    <>
      <div style={{ marginBottom: "1rem" }}>
        <Link to="/orders" className="pill">← Alle bestellingen</Link>
      </div>

      <div className="row2">
        <div className="panel">
          <div className="panel-h">
            <h3>{order.order_no}</h3>
            <span className={`pill-st ${order.status}`}>{statusLabel(order.status)}</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.4rem" }}>
            <div>
              <div className="kpi-l">Klant</div>
              <div style={{ fontFamily: "var(--f-block)", fontSize: "1rem" }}>
                {order.buyer_first_name} {order.buyer_last_name}
              </div>
              <div style={{ fontFamily: "var(--f-mono)", fontSize: "0.8rem", color: "rgba(255,244,214,0.7)" }}>
                {order.buyer_email}<br />
                {order.buyer_phone}<br />
                {order.buyer_company}
              </div>
            </div>
            <div>
              <div className="kpi-l">Flow</div>
              <div style={{ fontFamily: "var(--f-block)" }}>{flowLabel(order.flow)}</div>
              {order.discount_code && (
                <div style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
                  Korting: <code>{order.discount_code.code}</code>
                </div>
              )}
              {order.ambassador && (
                <div style={{ fontSize: "0.8rem" }}>
                  Ambassadeur: {order.ambassador.name}
                </div>
              )}
            </div>
          </div>

          {canEditSponsorInvoice && (
            <div style={{ marginBottom: "1.4rem", padding: "1rem 0", borderTop: "1px dashed rgba(255,122,26,0.25)", borderBottom: "1px dashed rgba(255,122,26,0.25)" }}>
              <div className="panel-h" style={{ padding: 0, marginBottom: "0.8rem" }}>
                <h3>Factuurgegevens</h3>
                {!sponsorInvoiceReady && <span className="pill-st cancelled">KvK/BTW vereist</span>}
              </div>
              <div className="form-grid">
                <div className="field"><label>Bedrijfsnaam *</label>
                  <input value={invoiceForm.company} onChange={(e) => setInvoiceForm({ ...invoiceForm, company: e.target.value })} />
                </div>
                <div className="field"><label>KvK *</label>
                  <input value={invoiceForm.kvk_number} onChange={(e) => setInvoiceForm({ ...invoiceForm, kvk_number: e.target.value })} />
                </div>
                <div className="field"><label>BTW-nr (of KvK)</label>
                  <input value={invoiceForm.vat_number} onChange={(e) => setInvoiceForm({ ...invoiceForm, vat_number: e.target.value })} />
                </div>
                <div className="field"><label>Straat + huisnummer *</label>
                  <input value={invoiceForm.street_and_number} onChange={(e) => setInvoiceForm({ ...invoiceForm, street_and_number: e.target.value })} />
                </div>
                <div className="field"><label>Postcode *</label>
                  <input value={invoiceForm.postal_code} onChange={(e) => setInvoiceForm({ ...invoiceForm, postal_code: e.target.value })} />
                </div>
                <div className="field"><label>Plaats *</label>
                  <input value={invoiceForm.city} onChange={(e) => setInvoiceForm({ ...invoiceForm, city: e.target.value })} />
                </div>
                <div className="field"><label>Land</label>
                  <input value={invoiceForm.country} onChange={(e) => setInvoiceForm({ ...invoiceForm, country: e.target.value.toUpperCase() })} />
                </div>
              </div>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.8rem" }}>
                <button
                  className="btn-admin ghost sm"
                  onClick={() => invoiceDetailsMut.mutate()}
                  disabled={invoiceDetailsMut.isPending || saveAndInvoiceMut.isPending || !sponsorInvoiceReady}
                >
                  Gegevens opslaan
                </button>
                {canCreateOrIssueInvoice && (
                  <button
                    className="btn-admin sm"
                    onClick={() => saveAndInvoiceMut.mutate()}
                    disabled={invoiceDetailsMut.isPending || saveAndInvoiceMut.isPending || !sponsorInvoiceReady}
                  >
                    Opslaan + factuur versturen
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="tbl-wrap" style={{ marginBottom: "1rem" }}>
            <table className="tbl">
              <thead><tr><th>Item</th><th>Aantal</th><th>Prijs</th><th>Totaal</th></tr></thead>
              <tbody>
                {order.items?.map((it: any) => (
                  <tr key={it.id}>
                    <td>{it.ticket_type?.name}</td>
                    <td>{it.qty}</td>
                    <td>{fmtEuro(it.unit_price_cents)}</td>
                    <td>{fmtEuro(it.line_total_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.4rem 2rem", maxWidth: "320px", marginLeft: "auto", fontFamily: "var(--f-mono)", fontSize: "0.9rem" }}>
            <span>Subtotaal</span><span>{fmtEuro(order.subtotal_cents)}</span>
            {order.discount_cents > 0 && (<><span>Korting</span><span>− {fmtEuro(order.discount_cents)}</span></>)}
            <span>BTW (incl.)</span><span>{fmtEuro(order.vat_cents)}</span>
            <span style={{ fontFamily: "var(--f-display)", fontSize: "1.2rem", color: "var(--c-orange)" }}>Totaal</span>
            <span style={{ fontFamily: "var(--f-display)", fontSize: "1.2rem", color: "var(--c-orange)" }}>{fmtEuro(order.total_cents)}</span>
          </div>

          <div style={{ marginTop: "1.4rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {order.status === "pending" && order.flow !== "sponsor" && !order.mollie_sales_invoice_id && (
              <button className="btn-admin sm" onClick={() => payLinkMut.mutate()} disabled={payLinkMut.isPending}>
                ✉ Stuur betaallink
              </button>
            )}
            {(order.status === "pending" || order.status === "invoice_sent" || order.status === "draft") && (
              <button
                className="btn-admin ghost sm"
                onClick={() => {
                  if (confirm(order.flow === "sponsor" || order.mollie_sales_invoice_id
                    ? "Markeer factuur als betaald + genereer tickets?"
                    : "Markeer als betaald (cash/bank ontvangen)? Genereert tickets + verstuurt bevestigingsmail.")) {
                    markPaidMut.mutate();
                  }
                }}
                disabled={markPaidMut.isPending}
              >
                {order.flow === "sponsor" || order.mollie_sales_invoice_id ? "✓ Factuur betaald" : "✓ Markeer betaald"}
              </button>
            )}
            {canCreateOrIssueInvoice && (
              <button className="btn-admin sm" onClick={() => invoiceMut.mutate()} disabled={invoiceMut.isPending || !sponsorInvoiceReady}>
                {order.mollie_sales_invoice_status === "draft" ? "📄 Verstuur conceptfactuur" : "📄 Verstuur Mollie factuur"}
              </button>
            )}
            {supportsBusinessInvoice(order.flow) && order.mollie_sales_invoice_id && order.status !== "invoice_paid" && order.status !== "paid" && (
              <button
                className="btn-admin ghost sm"
                onClick={() => syncInvoiceMut.mutate()}
                disabled={syncInvoiceMut.isPending}
                title="Vraag Mollie de actuele factuurstatus"
              >
                🔄 Sync factuurstatus
              </button>
            )}

            {/* Sync met Mollie — voor stuck pending orders waar webhook is gemist */}
            {order.mollie_payment_id && (order.status === "pending" || order.status === "draft") && (
              <button
                className="btn-admin ghost sm"
                onClick={() => syncMut.mutate()}
                disabled={syncMut.isPending}
                title="Vraag Mollie de huidige status — voor stuck orders"
              >
                🔄 Sync Mollie
              </button>
            )}

            {/* Refund — alleen bij paid/invoice_paid + werkelijke Mollie betaling */}
            {(order.status === "paid" || order.status === "invoice_paid") && order.mollie_payment_id && (
              <button
                className="btn-admin ghost sm"
                onClick={() => {
                  const totalEur = (order.total_cents / 100).toFixed(2).replace(".", ",");
                  const ans = prompt(
                    `Volledig of partial refund?\n\nLaat leeg voor volledig refund (€${totalEur}, tickets worden gevoid).\nVul een bedrag in € in voor partial refund (bv. 50,00).`,
                  );
                  if (ans === null) return;
                  let amountCents: number | undefined;
                  if (ans.trim()) {
                    const n = Number(ans.replace(",", "."));
                    if (isNaN(n) || n <= 0) { alert("Ongeldig bedrag."); return; }
                    amountCents = Math.round(n * 100);
                  }
                  const reason = prompt("Reden (optioneel, voor audit log):") ?? undefined;
                  if (!confirm(amountCents
                    ? `Refund €${(amountCents / 100).toFixed(2).replace(".", ",")} via Mollie?`
                    : `Volledig refund €${totalEur} via Mollie? Tickets worden gevoid.`)) return;
                  refundMut.mutate({ amountCents, reason });
                }}
                disabled={refundMut.isPending}
                title="Refund via Mollie API"
              >
                ↩ Refund
              </button>
            )}

            {/* Re-fulfill — voor backfill van orders zonder tickets/ambassadeur (oude bug) */}
            {(order.status === "paid" || order.status === "invoice_paid") && (
              <button
                className="btn-admin ghost sm"
                onClick={() => {
                  if (confirm("Re-fulfill: tickets opnieuw genereren + ambassadeur-record aanmaken indien nodig. Veilig (idempotent).")) {
                    refulfillMut.mutate();
                  }
                }}
                disabled={refulfillMut.isPending}
                title="Forceer fulfillment (idempotent — voor orders zonder tickets door oude bug)"
              >
                ⚙ Re-fulfill
              </button>
            )}

            {order.mollie_payment_url && (
              <a className="btn-admin ghost sm" href={order.mollie_payment_url} target="_blank" rel="noopener">
                Open Mollie checkout ↗
              </a>
            )}
          </div>

          {order.status === "pending" && order.flow !== "sponsor" && !order.mollie_sales_invoice_id && (
            <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px dashed rgba(255,122,26,0.2)" }}>
              <label style={{ fontSize: "0.78rem", opacity: 0.7, fontFamily: "var(--f-mono)" }}>
                Optioneel persoonlijk bericht in betaalmail
              </label>
              <textarea
                rows={2}
                value={payLinkMessage}
                onChange={(e) => setPayLinkMessage(e.target.value)}
                placeholder="bv. 'Hoi Henk, hier de link zoals besproken.'"
                style={{ width: "100%", marginTop: 4, fontSize: "0.85rem" }}
              />
            </div>
          )}
        </div>

        <div>
          <div className="panel">
            <div className="panel-h">
              <h3>Tickets ({order.tickets?.length ?? 0})</h3>
              {(order.tickets ?? []).length > 0 && (
                <div className="row-actions">
                  <button
                    type="button"
                    onClick={() => openTicketAccess({ orderId: order.id }, "page")}
                    disabled={ticketLinkBusy !== null}
                    title="Open de klantweergave met een tijdelijke admin-link"
                  >
                    {ticketLinkBusy === `page:${order.id}` ? "Openen…" : "Klantpagina"}
                  </button>
                  <button
                    type="button"
                    onClick={() => openTicketAccess({ orderId: order.id }, "pdf")}
                    disabled={ticketLinkBusy !== null}
                    title="Open de PDF van alle tickets in deze bestelling"
                  >
                    {ticketLinkBusy === `pdf:${order.id}` ? "Openen…" : "PDF alles"}
                  </button>
                </div>
              )}
            </div>
            {(order.tickets ?? []).length === 0 && <div className="empty">Nog geen tickets gegenereerd.</div>}
            {order.tickets?.map((t: any) => (
              <div key={t.id} style={{ padding: "0.6rem 0", borderBottom: "1px dashed rgba(255,122,26,0.2)" }}>
                <div style={{ fontFamily: "var(--f-mono)", color: "var(--c-orange)", fontSize: "0.78rem" }}>{t.ticket_no}</div>
                <div>{t.ticket_type?.name}</div>
                <span className={`pill-st ${t.status}`}>{statusLabel(t.status)}</span>
                {t.checked_in_at && <small style={{ display: "block", marginTop: "4px" }}>Gescand {fmtDateTime(t.checked_in_at)}</small>}
                <div className="row-actions" style={{ marginTop: "0.45rem" }}>
                  <button
                    type="button"
                    onClick={() => openTicketAccess({ ticketId: t.id }, "pdf")}
                    disabled={ticketLinkBusy !== null}
                    title="Open alleen dit ticket als PDF"
                  >
                    {ticketLinkBusy === `pdf:${t.id}` ? "Openen…" : "PDF ticket"}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="panel" style={{ marginTop: "1rem" }}>
            <div className="panel-h"><h3>Meta</h3></div>
            <div style={{ fontFamily: "var(--f-mono)", fontSize: "0.78rem", lineHeight: 1.8 }}>
              <div>Aangemaakt: {fmtDateTime(order.created_at)}</div>
              {order.paid_at && <div>Betaald: {fmtDateTime(order.paid_at)}</div>}
              {order.fulfilled_at && <div>Tickets gegenereerd: {fmtDateTime(order.fulfilled_at)}</div>}
              {order.invoice_no && <div>Factuurnr: {order.invoice_no}</div>}
              {order.invoice_due_at && <div>Vervalt: {fmtDateTime(order.invoice_due_at)}</div>}
              {order.mollie_payment_id && <div>Mollie: <code>{order.mollie_payment_id}</code></div>}
              {order.mollie_sales_invoice_id && (
                <div>Mollie factuur: <code>{order.mollie_sales_invoice_id}</code></div>
              )}
              {order.mollie_sales_invoice_status && (
                <div>Factuurstatus: {invoiceStatusLabel(order.mollie_sales_invoice_status)}</div>
              )}
              {order.mollie_sales_invoice_synced_at && (
                <div>Factuur sync: {fmtDateTime(order.mollie_sales_invoice_synced_at)}</div>
              )}
              {order.mollie_sales_invoice_url && (
                <div>
                  <a href={order.mollie_sales_invoice_url} target="_blank" rel="noopener">Open Mollie factuur ↗</a>
                </div>
              )}
              {order.source && <div>Bron: {order.source}</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginTop: "1rem" }}>
        <div className="panel-h"><h3>Verzonden mails ({orderEmails?.length ?? 0})</h3></div>
        {(orderEmails ?? []).length === 0 ? (
          <div className="empty">Nog geen mails voor deze bestelling.</div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Wanneer</th><th>Onderwerp</th><th>Template</th><th>Status</th><th>Opens · Clicks</th><th></th></tr>
              </thead>
              <tbody>
                {orderEmails!.map((s) => (
                  <tr key={s.id}>
                    <td>{fmtRelative(s.created_at)}</td>
                    <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.subject ?? <span style={{ opacity: 0.5 }}>—</span>}
                    </td>
                    <td>{s.template_slug ? <code>{s.template_slug}</code> : "—"}</td>
                    <td><span className={`pill-st ${emailStatusClass(s.status)}`}>{emailStatusLabel(s.status)}</span></td>
                    <td>
                      <small>
                        {s.open_count > 0 && <>👁 {s.open_count} </>}
                        {s.click_count > 0 && <>🖱 {s.click_count}</>}
                        {s.open_count === 0 && s.click_count === 0 && <span style={{ opacity: 0.4 }}>—</span>}
                      </small>
                    </td>
                    <td><Link to={`/emails/${s.id}`} className="btn-mini">Open</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {orderInbound && orderInbound.length > 0 && (
        <div className="panel" style={{ marginTop: "1rem" }}>
          <div className="panel-h"><h3>Antwoorden van klant ({orderInbound.length})</h3></div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Ontvangen</th><th>Van</th><th>Onderwerp</th><th></th></tr>
              </thead>
              <tbody>
                {orderInbound.map((m) => (
                  <tr key={m.id}>
                    <td>{fmtRelative(m.received_at)}</td>
                    <td>{m.from_name ?? m.from_email}</td>
                    <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.subject ?? "—"}
                    </td>
                    <td><Link to="/inbox" className="btn-mini">Inbox</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast ${toast.kind}`} onClick={() => setToast(null)}>
          {toast.text}
        </div>
      )}
    </>
  );
}
